import React, { useState } from "react";
import { motion } from "framer-motion";
import { Wallet, Store, ArrowRight, Loader2, ShieldCheck, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";

export default function RoleSelect() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [choice, setChoice] = useState(null); // null | "customer" | "merchant"
  const [busy, setBusy] = useState(false);
  const [biz, setBiz] = useState({ business_name: "", gst: "", pan: "", bank_account: "" });

  const pickCustomer = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/choose-role", { role: "customer" });
      setUser(data.user);
      toast.success("Welcome! Your wallet is ready.");
    } catch { toast.error("Something went wrong"); } finally { setBusy(false); }
  };

  const submitMerchant = async () => {
    if (!biz.business_name) return toast.error("Business name is required");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/choose-role", { role: "merchant", ...biz });
      setUser(data.user);
      toast.success("Merchant account created!");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-slate-950 paras-grid-bg text-slate-100">
      <div className="mx-auto flex min-h-screen max-w-3xl flex-col justify-center px-6 py-12">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/paras-logo.png" alt="Paras CashBack" className="h-9 w-9 object-contain" />
            <span className="font-heading text-lg font-bold">Paras CashBack</span>
          </div>
          <button data-testid="onboarding-logout" onClick={doLogout} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-rose-300">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>

        <div className="mb-2 inline-flex w-fit items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-mono uppercase tracking-widest text-emerald-400">
          <ShieldCheck className="h-3.5 w-3.5" /> Hi {user?.name?.split(" ")[0] || "there"}
        </div>
        <h1 className="font-heading text-3xl font-extrabold sm:text-4xl">How will you use Paras CashBack?</h1>
        <p className="mt-2 text-sm text-slate-400">Pick one to get started. You can always contact us to change this later.</p>

        {choice !== "merchant" ? (
          <div className="mt-10 grid gap-5 sm:grid-cols-2">
            <motion.button
              data-testid="role-card-customer"
              whileHover={{ y: -4 }} onClick={pickCustomer} disabled={busy}
              className="ledger-card group rounded-2xl p-7 text-left transition disabled:opacity-60"
            >
              <Wallet className="h-9 w-9 text-emerald-400" />
              <h2 className="mt-4 font-heading text-xl font-bold">I'm a Customer</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">Collect cashback from stores, track pending vs available balance, and redeem to your account.</p>
              <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-emerald-400">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" /></>}
              </span>
            </motion.button>

            <motion.button
              data-testid="role-card-merchant"
              whileHover={{ y: -4 }} onClick={() => setChoice("merchant")} disabled={busy}
              className="ledger-card group rounded-2xl p-7 text-left ring-1 ring-emerald-500/20 transition disabled:opacity-60"
            >
              <Store className="h-9 w-9 text-emerald-400" />
              <h2 className="mt-4 font-heading text-xl font-bold">I'm a Merchant</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">Subscribe to a plan, get cashback tokens, and reward your customers on every real purchase.</p>
              <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-emerald-400">
                Set up business <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
              </span>
            </motion.button>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-10 ledger-card rounded-2xl p-8">
            <div className="flex items-center gap-2"><Store className="h-5 w-5 text-emerald-400" /><h2 className="font-heading text-xl font-bold">Tell us about your business</h2></div>
            <div className="mt-6 space-y-3">
              {[["business_name", "Business name *"], ["gst", "GSTIN (optional)"], ["pan", "PAN (optional)"], ["bank_account", "Bank account (optional)"]].map(([k, l]) => (
                <input key={k} data-testid={`onboard-biz-${k}`} placeholder={l} value={biz[k]}
                  onChange={(e) => setBiz({ ...biz, [k]: e.target.value })}
                  className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm outline-none focus:border-emerald-500" />
              ))}
            </div>
            <div className="mt-6 flex gap-3">
              <button data-testid="onboard-merchant-submit" onClick={submitMerchant} disabled={busy}
                className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-2.5 font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Create merchant account
              </button>
              <button onClick={() => setChoice(null)} className="rounded-full border border-slate-700 px-5 py-2.5 text-sm hover:border-slate-500">Back</button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
